import { ImageUpload } from "@/components/admin/ImageUpload";
import { TagInput } from "@/components/admin/TagInput";
import { Field, btnSmall, inputCls } from "@/components/admin/ui";

const update = (list, i, changes) => list.map((item, idx) => (idx === i ? { ...item, ...changes } : item));
const remove = (list, i) => list.filter((_, idx) => idx !== i);

const StylePicker = ({ person, options, onChange, testId }) => (
  <TagInput
    value={options.filter((o) => (person.dance_styles || []).includes(o.name)).map((o) => o.id)}
    options={options}
    onChange={(ids) => onChange({ dance_styles: ids.slice(0, 3).map((id) => options.find((o) => o.id === id)?.name).filter(Boolean) })}
    placeholder="Dance styles (up to 3)"
    testId={testId}
  />
);

export const PeopleEditor = ({ title, nameLabel, items, onChange, testId, styleOptions }) => (
  <div data-testid={testId}>
    <div className="flex items-center justify-between mb-3">
      <div>
        <p className="text-[15px] font-semibold">{title}</p>
        {styleOptions && <p className="text-[12px] text-[#6B7280]">Up to 3 dance styles each — shown under the name on the festival page.</p>}
      </div>
      <button type="button" className={btnSmall} onClick={() => onChange([...items, { name: "", location: "", dance_styles: [], image_url: null }])} data-testid={`${testId}-add`}>
        + Add {nameLabel.toLowerCase()}
      </button>
    </div>
    {items.length === 0 && <p className="text-[12px] text-[#6B7280]">None added yet.</p>}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {items.map((p, i) => (
        <div key={i} className="flex gap-3 rounded-lg border border-[#E5E7EB] p-3" data-testid={`${testId}-item`}>
          <ImageUpload value={p.image_url} onChange={(url) => onChange(update(items, i, { image_url: url }))} aspect="285 / 405" hint="Cover 285×405" className="w-[110px] shrink-0" testId={`${testId}-image-${i}`} />
          <div className="flex-1 min-w-0 space-y-2">
            <input value={p.name} onChange={(e) => onChange(update(items, i, { name: e.target.value }))} placeholder={nameLabel} className={inputCls} data-testid={`${testId}-name-${i}`} />
            {styleOptions ? (
              <StylePicker person={p} options={styleOptions} onChange={(changes) => onChange(update(items, i, changes))} testId={`${testId}-styles-${i}`} />
            ) : (
              <input value={p.location} onChange={(e) => onChange(update(items, i, { location: e.target.value }))} placeholder="Location" className={inputCls} data-testid={`${testId}-location-${i}`} />
            )}
            <button type="button" className="text-[12px] text-[#6B7280] hover:text-[#B91C1C]" onClick={() => onChange(remove(items, i))} data-testid={`${testId}-remove-${i}`}>
              Remove
            </button>
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const PassesEditor = ({ passes, onChange }) => (
  <div data-testid="passes-editor">
    <div className="flex items-center justify-between mb-3">
      <div>
        <p className="text-[15px] font-semibold">Passes</p>
        <p className="text-[12px] text-[#6B7280]">Shown as pricing cards on the watch page. "Purchase Pass" opens Stripe Checkout using the price below (e.g. $150 or €150). Up to 5 features each; a check mark is added automatically.</p>
      </div>
      <button type="button" className={btnSmall} onClick={() => onChange([...passes, { tier_name: "", price: "", features: [""], link: "", highlighted: false }])} data-testid="passes-add">
        + Add tier
      </button>
    </div>
    {passes.length === 0 && <p className="text-[12px] text-[#6B7280]">No pass tiers yet.</p>}
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {passes.map((p, i) => (
        <div key={i} className={`rounded-lg border p-3 space-y-2 ${p.highlighted ? "border-[#F8D481] border-2" : "border-[#E5E7EB]"}`} data-testid={`pass-editor-${i}`}>
          <input value={p.tier_name} onChange={(e) => onChange(update(passes, i, { tier_name: e.target.value }))} placeholder="Tier name" className={inputCls} data-testid={`pass-tier-name-${i}`} />
          <input value={p.price} onChange={(e) => onChange(update(passes, i, { price: e.target.value }))} placeholder="Price (e.g. $150)" className={inputCls} data-testid={`pass-price-${i}`} />
          <div className="space-y-1.5">
            {p.features.map((f, fi) => (
              <div key={fi} className="flex items-center gap-1.5">
                <span className="text-[13px]">✓</span>
                <input value={f} onChange={(e) => onChange(update(passes, i, { features: p.features.map((x, k) => (k === fi ? e.target.value : x)) }))} placeholder={`Feature ${fi + 1}`} className={`${inputCls} h-9`} data-testid={`pass-feature-${i}-${fi}`} />
                <button type="button" className="text-[#9CA3AF] hover:text-[#B91C1C] text-[16px] leading-none px-1" onClick={() => onChange(update(passes, i, { features: p.features.filter((_, k) => k !== fi) }))} aria-label="Remove feature">×</button>
              </div>
            ))}
            {p.features.length < 5 && (
              <button type="button" className="text-[12px] text-[#3B5BDB] hover:underline" onClick={() => onChange(update(passes, i, { features: [...p.features, ""] }))} data-testid={`pass-add-feature-${i}`}>
                + Add feature
              </button>
            )}
          </div>
          <input value={p.link} onChange={(e) => onChange(update(passes, i, { link: e.target.value }))} placeholder="External checkout link (optional, overrides Stripe)" className={inputCls} data-testid={`pass-link-${i}`} />
          <div className="flex items-center justify-between pt-1">
            <label className="flex items-center gap-1.5 text-[12px] cursor-pointer">
              <input type="checkbox" checked={p.highlighted} onChange={(e) => onChange(passes.map((x, k) => ({ ...x, highlighted: k === i ? e.target.checked : false })))} className="accent-[#B644DC]" data-testid={`pass-highlight-${i}`} />
              Highlight tier
            </label>
            <button type="button" className="text-[12px] text-[#6B7280] hover:text-[#B91C1C]" onClick={() => onChange(remove(passes, i))} data-testid={`pass-remove-${i}`}>Remove</button>
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const VenueEditor = ({ venue, onChange }) => (
  <div className="space-y-4" data-testid="venue-editor">
    <p className="text-[15px] font-semibold">The Venue</p>
    <ImageUpload value={venue.image_url} onChange={(url) => onChange({ ...venue, image_url: url })} aspect="1132 / 405" hint="Venue image 1132×405 (landscape)" testId="venue-image" />
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field label="Venue name"><input value={venue.name || ""} onChange={(e) => onChange({ ...venue, name: e.target.value })} className={inputCls} placeholder="Sherbrooke Hotel" data-testid="venue-name" /></Field>
      <Field label="Location"><input value={venue.location || ""} onChange={(e) => onChange({ ...venue, location: e.target.value })} className={inputCls} placeholder="Street, City, Province" data-testid="venue-location" /></Field>
    </div>
    <Field label="Description">
      <textarea value={venue.description || ""} onChange={(e) => onChange({ ...venue, description: e.target.value })} className={`${inputCls} h-auto py-2 min-h-[90px] resize-y`} data-testid="venue-description" />
    </Field>
  </div>
);
